import { useQueryClient } from '@tanstack/react-query';
import type { ChangePasswordInput, LoginInput, UserSummary } from 'chery-contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authTokenStore } from '../apis/auth-token';
import { onSessionChange, orpcClient } from '../apis/orpc-client';

export interface AuthContextValue {
  user: UserSummary | null;
  isAuthenticated: boolean;
  /** True until the boot-time `auth.me` call settles, so routes do not redirect prematurely. */
  isBootstrapping: boolean;
  login: (input: LoginInput) => Promise<UserSummary>;
  changePassword: (input: ChangePasswordInput) => Promise<UserSummary>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserSummary | null>(() => authTokenStore.getUser());
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => authTokenStore.hasSession());

  // A silent refresh in the oRPC link (or a refused one) is reflected here.
  useEffect(() => onSessionChange((session) => setUser(session ? session.user : null)), []);

  // On boot, a stored token is only a hint — confirm it with the service before trusting it. An expired access
  // token is renewed transparently by the link's refresh-on-401.
  useEffect(() => {
    if (!authTokenStore.hasSession()) {
      setIsBootstrapping(false);

      return;
    }

    let isCancelled = false;

    const verifySession = async (): Promise<void> => {
      try {
        const me = await orpcClient.auth.me();

        if (isCancelled) {
          return;
        }

        authTokenStore.setUser(me);
        setUser(me);
      } catch {
        if (isCancelled) {
          return;
        }

        authTokenStore.clear();
        setUser(null);
      } finally {
        if (!isCancelled) {
          setIsBootstrapping(false);
        }
      }
    };

    void verifySession();

    return () => {
      isCancelled = true;
    };
  }, []);

  const login = useCallback(
    async (input: LoginInput): Promise<UserSummary> => {
      const session = await orpcClient.auth.login(input);

      authTokenStore.setSession(session);
      setUser(session.user);
      setIsBootstrapping(false);
      await queryClient.invalidateQueries();

      return session.user;
    },
    [queryClient],
  );

  const changePassword = useCallback(
    async (input: ChangePasswordInput): Promise<UserSummary> => {
      const session = await orpcClient.auth.changePassword(input);

      authTokenStore.setSession(session);
      setUser(session.user);
      await queryClient.invalidateQueries();

      return session.user;
    },
    [queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = authTokenStore.getRefreshToken();

    authTokenStore.clear();
    setUser(null);
    queryClient.clear();

    if (refreshToken) {
      // Best effort: the local session is gone either way.
      await orpcClient.auth.logout({ refreshToken }).catch(() => undefined);
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isBootstrapping,
      login,
      changePassword,
      logout,
    }),
    [user, isBootstrapping, login, changePassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }

  return context;
};
